import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
  LegacyRef,
} from "react";
import { useTranslation } from "react-i18next";
import { useForm, FormProvider, Controller } from "react-hook-form";

// Store
import { useAlertsStore } from "../../../store/alerts";
import { useAlertStore } from "../../../store/alertStore";

// Utils
import { isValidUrlWithTLD } from "../../../setup/util/urlValidation";

// API
import { getAlertById } from "../../../setup/api/services/alerts";

// Types
import {
  ICreateAlertBody,
  EAlertSeverity,
  EAlertSurface,
} from "../../../setup/api/services/alerts/models";

// Components
import RadioButtonGroup from "../../common/RadioButton/RadioButtonGroup";
import Checkbox from "../../common/hookForm/Checkbox";
import TextInput from "../../common/hookForm/TextInput";
import TextArea from "../../common/hookForm/TextArea";
import IosSwitch from "../../common/hookForm/IosSwitch";
import AlertWidget from "../../common/AlertWidget/AlertWidget";

// Styles
import styles from "./CreateAlertModal.module.scss";
import PrimaryButton, {
  PrimaryButtonSize,
  PrimaryButtonType,
} from "../../common/PrimaryButton";

// ============================================================================
// INTERFACES
// ============================================================================

interface Props {
  title: string;
}

interface FormData {
  messageAr: string;
  severity: EAlertSeverity;
  ctaEnabled: boolean;
  ctaLabel: string;
  ctaUrl: string;
  webPlatform: boolean;
  androidPlatform: boolean;
  iosPlatform: boolean;
}

export interface CreateAlertModalRef {
  openModal: (alertId?: string) => void;
  closeModal: () => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

const CreateAlertModal = forwardRef<CreateAlertModalRef, Props>((_, ref) => {
  const { t } = useTranslation();

  // ========================================================================
  // STATE
  // ========================================================================

  const [open, setOpen] = useState<boolean>(false);
  const [editAlertId, setEditAlertId] = useState<string | null>(null);
  const [loadingAlert, setLoadingAlert] = useState<boolean>(false);
  const { createAlert, updateAlert, loading, fetchAlerts } = useAlertsStore();
  const { showError, showSuccess } = useAlertStore();

  // ========================================================================
  // FORM SETUP
  // ========================================================================

  const methods = useForm<FormData>({
    defaultValues: {
      messageAr: "",
      severity: EAlertSeverity.INFO,
      ctaEnabled: false,
      ctaLabel: "",
      ctaUrl: "",
      webPlatform: false,
      androidPlatform: false,
      iosPlatform: false,
    },
  });

  const {
    control,
    reset,
    handleSubmit,
    watch,
    formState: { errors },
  } = methods;
  const ctaEnabled = watch("ctaEnabled");
  const messageAr = watch("messageAr");
  const ctaLabel = watch("ctaLabel");
  const ctaUrl = watch("ctaUrl");
  const severity = watch("severity");

  // Check if form is ready to submit
  const isFormValid =
    messageAr &&
    messageAr.trim() !== "" &&
    messageAr.length <= 90 && // Add 90 character limit check
    (!ctaEnabled ||
      (ctaLabel && ctaLabel.trim() !== "" && ctaUrl && ctaUrl.trim() !== ""));

  // ========================================================================
  // CONSTANTS
  // ========================================================================

  const severityOptions = [
    { value: EAlertSeverity.INFO, label: t("alert_information") },
    { value: EAlertSeverity.WARNING, label: t("alert_warning") },
    { value: EAlertSeverity.CRITICAL, label: t("alert_emergency") },
  ];

  // ========================================================================
  // HANDLERS
  // ========================================================================

  const closeModal = useCallback(() => {
    setOpen(false);
    setEditAlertId(null);
    reset({
      messageAr: "",
      severity: EAlertSeverity.INFO,
      ctaEnabled: false,
      ctaLabel: "",
      ctaUrl: "",
      webPlatform: false,
      androidPlatform: false,
      iosPlatform: false,
    });
  }, [reset]);

  const openModal = useCallback(
    async (alertId?: string) => {
      setOpen(true);
      if (alertId) {
        setEditAlertId(alertId);
        setLoadingAlert(true);
        try {
          const alertData = await getAlertById(alertId);
          // Prefill form with alert data
          reset({
            messageAr: alertData.messageAr,
            severity: alertData.severity,
            ctaEnabled: alertData.cta.enabled,
            ctaLabel: alertData.cta.label || "",
            ctaUrl: alertData.cta.url || "",
            webPlatform: alertData.surfaces.includes(EAlertSurface.WEB),
            androidPlatform: alertData.surfaces.includes(EAlertSurface.ANDROID),
            iosPlatform: alertData.surfaces.includes(EAlertSurface.IOS),
          });
        } catch (error) {
          console.error("Failed to load alert:", error);
          showError(t("alert_fetch_error"));
          closeModal();
        } finally {
          setLoadingAlert(false);
        }
      } else {
        // For new alerts, set all platforms to checked by default
        reset({
          messageAr: "",
          severity: EAlertSeverity.INFO,
          ctaEnabled: false,
          ctaLabel: "",
          ctaUrl: "",
          webPlatform: true,
          androidPlatform: true,
          iosPlatform: true,
        });
      }
    },
    [reset, showError, t, closeModal]
  );

  const stopPropagation = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  const onSubmit = useCallback(
    async (data: FormData) => {
      try {
        // Validate platforms - at least one must be selected
        const selectedSurfaces: EAlertSurface[] = [];
        if (data.webPlatform) selectedSurfaces.push(EAlertSurface.WEB);
        if (data.androidPlatform) selectedSurfaces.push(EAlertSurface.ANDROID);
        if (data.iosPlatform) selectedSurfaces.push(EAlertSurface.IOS);

        if (selectedSurfaces.length === 0) {
          showError(t("alert_platform_required"));
          return;
        }

        // Prepare alert data for API
        const alertData: ICreateAlertBody = {
          messageAr: data.messageAr,
          severity: data.severity,
          ctaEnabled: data.ctaEnabled,
          surfaces: selectedSurfaces,
          // Include CTA fields only if enabled
          ...(data.ctaEnabled && {
            ctaLabel: data.ctaLabel,
            ctaUrl: data.ctaUrl,
          }),
        };

        let result;
        if (editAlertId) {
          // Update existing alert
          result = await updateAlert(editAlertId, {
            id: editAlertId,
            ...alertData,
          });
        } else {
          // Create new alert
          result = await createAlert(alertData);
        }

        if (result) {
          const successKey = editAlertId
            ? "alert_update_success"
            : "alert_create_success";
          showSuccess(t(successKey));
          await fetchAlerts(); // Refresh the list
          closeModal();
        } else {
          const errorKey = editAlertId
            ? "alert_update_error"
            : "alert_create_error";
          showError(t(errorKey));
        }
      } catch (error) {
        console.error(
          `Failed to ${editAlertId ? "update" : "create"} alert:`,
          error
        );
        const errorKey = editAlertId
          ? "alert_update_error_occurred"
          : "alert_create_error_occurred";
        showError(t(errorKey));
      }
    },
    [
      createAlert,
      updateAlert,
      closeModal,
      editAlertId,
      showSuccess,
      showError,
      t,
      fetchAlerts,
    ]
  );

  // ========================================================================
  // IMPERATIVE HANDLE
  // ========================================================================

  useImperativeHandle(ref, () => ({
    openModal,
    closeModal,
  }));

  // ========================================================================
  // RENDER
  // ========================================================================

  if (!open) return null;

  return (
    <div ref={ref as LegacyRef<HTMLDivElement>} className={styles.modal}>
      {/* Backdrop */}
      <div onClick={closeModal} className={styles.mask} />

      {/* Modal Content */}
      <div onClick={stopPropagation} className={styles.content}>
        {/* Header with Close Button */}
        <div className={styles.header}>
          <h2 className={styles.title}>
            {editAlertId
              ? t("alert_edit_title")
              : t("create_alert_modal_title")}
          </h2>
          <div onClick={closeModal} className={styles.close_button}>
            <img src="/assets/images/svg/cross_icon.svg" alt="close" />
          </div>
        </div>

        {/* Form */}
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
            {/* Section 1: Alert Type/Severity */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>{t("alert_type")}</h3>
              <RadioButtonGroup
                name="severity"
                control={control}
                options={severityOptions}
                rules={{ required: "Please select an alert type" }}
              />
            </div>

            {/* Section 2: Alert Message */}
            <div className={styles.section}>
              <Controller
                name="messageAr"
                control={control}
                rules={{
                  required: t("alert_message_required"),
                  maxLength: {
                    value: 90,
                    message: t("alert_address_error"),
                  },
                  validate: (value) =>
                    value.trim() !== "" || t("alert_message_required"),
                }}
                render={({ field }) => (
                  <TextArea
                    {...field}
                    label={t("alert_address")}
                    rows={4}
                    placeholder={t("alert_scheduled_maintenance_message")}
                    maxLength={90}
                    showMaxLength={true}
                    showLabel={true}
                    showError={true}
                    errorMessage={t("alert_address_error")}
                  />
                )}
              />
            </div>

            {/* Section 3: Action Button (CTA) */}
            <div className={styles.section}>
              <div className={styles.toggleSection}>
                {/* CTA Toggle */}
                <Controller
                  name="ctaEnabled"
                  control={control}
                  render={({ field }) => (
                    <div className={styles.toggleContainer}>
                      <h3 className={styles.sectionTitle}>
                        {t("alert_action_button")}
                      </h3>
                      <IosSwitch
                        status={field?.value ? "on" : "off"}
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        rtl
                      />
                    </div>
                  )}
                />

                {/* CTA Fields (shown only when enabled) */}
                {ctaEnabled && (
                  <div className={styles.ctaFields}>
                    {/* Button Label */}
                    <Controller
                      name="ctaLabel"
                      control={control}
                      rules={{
                        required: ctaEnabled
                          ? t("alert_cta_label_required")
                          : false,
                        validate: (value) =>
                          !ctaEnabled ||
                          value.trim() !== "" ||
                          t("alert_cta_label_required"),
                      }}
                      render={({ field }) => (
                        <div className={styles.inputWrapper}>
                          <TextInput
                            {...field}
                            placeholder={t("alert_button_content")}
                            error={errors.ctaLabel?.message}
                          />
                        </div>
                      )}
                    />

                    {/* Button URL */}
                    <Controller
                      name="ctaUrl"
                      control={control}
                      rules={{
                        required: ctaEnabled
                          ? t("alert_cta_url_required")
                          : false,
                        validate: (value) => {
                          if (!ctaEnabled) return true;
                          if (!value || value.trim() === "")
                            return t("alert_cta_url_required");

                          if (!isValidUrlWithTLD(value)) {
                            return t("alert_cta_url_invalid");
                          }

                          return true;
                        },
                      }}
                      render={({ field }) => (
                        <div className={styles.inputWrapper}>
                          <TextInput
                            {...field}
                            placeholder={t("alert_url_link")}
                            error={errors.ctaUrl?.message}
                          />
                        </div>
                      )}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Section 4: Platform Selection */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>{t("alert_platforms")}</h3>
              <div className={styles.platformsContainer}>
                <Checkbox name="webPlatform" control={control} label="Web" />
                <Checkbox
                  name="androidPlatform"
                  control={control}
                  label="Android"
                />
                <Checkbox name="iosPlatform" control={control} label="iOS" />
              </div>
            </div>

            {/* Section 5: Live Preview */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>
                {t("alert_preview_alert")}
              </h3>
              <div>
                {messageAr && (
                  <AlertWidget
                    activeAlertWidget={{
                      id: 0,
                      cta: {
                        enabled: ctaEnabled,
                        url: ctaUrl || "",
                        label: ctaLabel || "",
                      },
                      severity: severity,
                      createdAt: "",
                      updatedAt: "",
                      messageAr: messageAr,
                      surfaces: [],
                      status: "ACTIVE",
                    }}
                  />
                )}
                {!messageAr && (
                  <div className={styles.previewPlaceholder}>
                    {t("alert_preview_placeholder") ||
                      "Enter a message to see preview"}
                  </div>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <PrimaryButton
              type="submit"
              buttonType={PrimaryButtonType.PRIMARY_DARK}
              size={PrimaryButtonSize.MEDIUM}
              disabled={loading || loadingAlert || !isFormValid}>
              {loading || loadingAlert
                ? t("lbl_loading")
                : editAlertId
                ? t("alert_update")
                : t("add")}
            </PrimaryButton>
          </form>
        </FormProvider>
      </div>
    </div>
  );
});

CreateAlertModal.displayName = "CreateAlertModal";

export default CreateAlertModal;
